import { useState, useEffect, useCallback } from 'react';
import { useRownd } from '@rownd/react';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Drawer, 
  IconButton, 
  Toolbar, 
  Typography, 
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  Avatar,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ReportIcon from '@mui/icons-material/Report';
import MapIcon from '@mui/icons-material/Map';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
// Import the logo image
import wavelengthLogo from '../assets/images/wavelength-logo.png';

const drawerWidth = 240;

export default function MainLayout({ children, onNavigate }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { is_authenticated, is_initializing, user, requestSignIn, signOut, manageAccount } = useRownd();
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  
  // Use memoized version of manageAccount to prevent potential issues
  const handleManageAccount = useCallback(() => {
    try {
      manageAccount();
    } catch (error) {
      console.error("Error opening account manager:", error);
    }
  }, [manageAccount]);

  // Use memoized version of signOut to prevent potential issues
  const handleSignOut = useCallback(() => {
    try {
      signOut();
    } catch (error) {
      console.error("Error signing out:", error);
      // Force reload the page if sign out fails
      window.location.reload();
    }
  }, [signOut]);
  
  // Check if user has access to admin page based on email domain
  useEffect(() => {
    if (is_authenticated && user) {
      // Try different methods of accessing the email field
      const emailFromData = user.data?.email;
      const emailDirectly = user.email;
      
      // Use the email field based on what's available
      const userEmail = emailFromData || emailDirectly;
      
      if (userEmail) {
        const email = userEmail.toLowerCase();
        const adminDomains = ['.mil', '.gov', '.teambespin.us'];
        
        // Check for exact domain matches and the special case for @teambespin.us
        const hasPermission = adminDomains.some(domain => {
          if (domain === '.teambespin.us' && email.includes('@teambespin.us')) {
            return true;
          }
          return email.endsWith(domain);
        });
        
        setHasAdminAccess(hasPermission);
      } else {
        setHasAdminAccess(false);
      }
    } else {
      setHasAdminAccess(false);
    }
  }, [is_authenticated, user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center' }}>
          WAVELENGTH: CTTS
          <img 
            src={wavelengthLogo} 
            alt="Wavelength Logo" 
            style={{ height: '48px', marginLeft: '8px' }} 
          />
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* Always show Map and Report */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation('map')}>
            <ListItemIcon>
              <MapIcon />
            </ListItemIcon>
            <ListItemText primary="Map" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation('report')}>
            <ListItemIcon>
              <ReportIcon />
            </ListItemIcon>
            <ListItemText primary="Report" />
          </ListItemButton>
        </ListItem>
        
        {/* Only show Admin option if user is signed in with an approved email domain */}
        {is_authenticated && hasAdminAccess && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('admin')}>
              <ListItemIcon>
                <AdminPanelSettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Admin" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      <Divider />
      <List>
        {is_authenticated ? (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={handleManageAccount}>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText primary="Account" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleSignOut}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <span>
              <ListItemButton onClick={() => requestSignIn()} disabled={is_initializing}>
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText primary="Sign In" />
              </ListItemButton>
            </span>
          </ListItem>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '70px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, alignItems: 'center' }}>
                WAVELENGTH: CROWDSOURCED TSUNAMI TRACKING SYSTEM
                <img 
                  src={wavelengthLogo} 
                  alt="Wavelength Logo" 
                  style={{ height: '63px', marginLeft: '12px' }} 
                />
              </Box>
              <Box sx={{ display: { xs: 'flex', sm: 'flex', md: 'none' }, alignItems: 'center' }}>
                WAVELENGTH: CTTS
                <img 
                  src={wavelengthLogo} 
                  alt="Wavelength Logo" 
                  style={{ height: '53px', marginLeft: '8px' }} 
                />
              </Box>
            </Typography>
          </Box>
          
          {/* Authentication Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {is_authenticated && (
              <Tooltip title="Manage Account">
                <Chip
                  avatar={<Avatar>{user.data?.first_name?.[0] || user.data?.email?.[0] || 'U'}</Avatar>}
                  label={user.data?.first_name || user.data?.email || 'User'}
                  onClick={handleManageAccount}
                  color="primary"
                  sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                />
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
} 